import useForm from "pages/components/Form/useForm";
import FormFields from "pages/components/FormFields";
import { useNavigate, useParams } from "react-router-dom";
import { Grid2, Typography, useTheme } from "@mui/material";
import { useEffect } from "react";

import { GppBad, GppGood } from "@mui/icons-material";
import { Box, Paper } from "@mui/material";
import ResetPasswordFormConfig from "./ResetPasswordFormConfig";
import { useTranslation } from "react-i18next";
import { ResetPasswordRequest } from "Domain/User/Dto/ResetPasswordRequest";
import { ResetUserPassword } from "Application/User/RegisterService";
import { getUrl, LOGIN } from "Infrastructure/config/routesConfig";
import { ConstraintMessage } from "pages/components/Form/FormEntities";

export default function ResetPasswordForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  ResetPasswordFormConfig["token"] = {
    default: token,
  };

  function handleSubmit(event: any) {
    event.preventDefault();
    if (isValidForm()) {
      ResetUserPassword(getResetPasswordRequest(values)).then((res: any) => {
        if (!res.error) {
          navigate(getUrl(LOGIN));
        }
      });
    }
  }

  const { errors, values, isValidForm, handleInputChange, Form } = useForm(
    ResetPasswordFormConfig,
    false
  );

  useEffect(() => {
    isValidForm();
  }, [values.password, values.passwordConfirmation]);

  return (
    <>
      <Typography variant="body1">{t("PUBLIC.text_reset_password")}</Typography>
      <FormConstraints errors={errors} />
      <Form onSubmit={handleSubmit}>
        <Grid2
          display={"grid"}
          gridTemplateColumns={"1fr"}
          gap={2}
          justifyItems={"center"}
        >
          <FormFields.Password
            name="password"
            type="password"
            value={values.password}
            onChange={handleInputChange}
            required
            error={"password" in errors}
          />
          <FormFields.Password
            name="passwordConfirmation"
            type="password"
            value={values.passwordConfirmation}
            onChange={handleInputChange}
            onPaste={(e: any) => {
              e.preventDefault();
            }}
            required
            error={"passwordConfirmation" in errors}
          />
        </Grid2>
        <FormFields.Button
          label={t("FORM.SUBMIT.send")}
          type="submit"
          fullWidth
          sx={{ mt: 2 }}
        />
      </Form>
    </>
  );
}

function FormConstraints({ errors }: any) {
  const { t } = useTranslation();

  const theme = useTheme();
  const errorColor = theme.palette.error.main;
  const successColor = theme.palette.success.main;

  const constraintsObjects: Array<{
    message: ConstraintMessage;
    isValid: boolean;
  }> = [];

  for (let field in ResetPasswordFormConfig) {
    const AllConstraints = ResetPasswordFormConfig[field].constraints;
    if (AllConstraints) {
      AllConstraints.forEach((constraint) => {
        if (typeof constraint !== "string") {
          constraintsObjects.push({
            message: constraint.message,
            isValid: !errors[field]?.includes(constraint.message),
          });
        }
      });
    }
  }

  return (
    <Paper
      elevation={1}
      sx={{
        marginTop: 2,
        marginBottom: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 2,
      }}
    >
      <Box display={"flex"} flexDirection={"column"} gap={1}>
        {constraintsObjects.map((constraintsObject, i) => (
          <Box
            key={i}
            display={"flex"}
            justifyItems={"center"}
            gap={1}
            sx={{
              color: constraintsObject.isValid ? successColor : errorColor,
            }}
          >
            {constraintsObject.isValid ? (
              <GppGood color="success" />
            ) : (
              <GppBad color="error" />
            )}
            {t(`FORM.ERRORS.${constraintsObject.message}`)}
            <br />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function getResetPasswordRequest(values: any) {
  return new ResetPasswordRequest(values.password, values.token);
}
